import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, CheckCircle, Star, Home, Building2, Calculator as CalcIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { products, testimonials, faqs, customerSegments, blogPosts, services, gallery, companyInfo } from '../mockData';
import SEO from '../components/SEO';

const Homepage = () => {
  // FAQ schema for SEO rich snippets
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer }
    }))
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Midtown Earth Solutions | Landscaping Materials & Delivery Columbus GA"
        description="Premium gravel, 57 stone, sand, mulch, topsoil & fill dirt delivered in Columbus, GA. Residential & commercial. Call (706) 366-3685 for a free quote."
        keywords="landscaping materials Columbus GA, gravel delivery, 57 stone, mulch delivery, topsoil, fill dirt, sand Columbus GA"
        path="/"
        jsonLd={faqSchema}
      />
      {/* Hero Section */}
      <section
        className="relative min-h-[520px] sm:min-h-[600px] lg:min-h-[700px] flex items-center justify-center overflow-hidden py-16 sm:py-20"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.5)), url('https://images.unsplash.com/photo-1769629918261-92d1fac176cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxkdW1wJTIwdHJ1Y2slMjBtYXRlcmlhbCUyMHlhcmR8ZW58MHx8fHwxNzgwMTg4Njk3fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h1 className="text-white text-3xl sm:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
            Premium Landscaping Materials<br />
            <span className="text-green-400">Delivered to Your Doorstep</span>
          </h1>
          <p className="text-gray-200 text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 max-w-3xl mx-auto">
            High-quality gravel, stone, sand, mulch, topsoil, and fill dirt with reliable delivery
            throughout Columbus, GA and surrounding areas.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
            <Link to="/contact" className="w-full sm:w-auto">
              <Button size="lg" className="bg-green-700 hover:bg-green-800 text-white text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto">
                Request a Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+17063663685" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="bg-white hover:bg-gray-100 text-gray-900 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 border-2 w-full sm:w-auto">
                <Phone className="mr-2 w-5 h-5" />
                Call (706) 366-3685
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Premium Quality</h3>
              <p className="text-gray-600">Only the finest landscaping materials sourced and inspected for your projects.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Fast Delivery</h3>
              <p className="text-gray-600">Same-day and next-day delivery available. We respect your schedule.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Competitive Pricing</h3>
              <p className="text-gray-600">Fair, transparent pricing with no hidden fees. Get the best value for your investment.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve - Residential & Commercial */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Who We Serve</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From homeowners to large commercial projects, we deliver quality materials to all customer types.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {customerSegments.map((segment, index) => (
              <Card key={segment.id} className="border-2 border-gray-200 hover:border-green-800 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-green-800 rounded-full flex items-center justify-center mb-6">
                    {index === 0 ? (
                      <Home className="w-8 h-8 text-white" />
                    ) : (
                      <Building2 className="w-8 h-8 text-white" />
                    )}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{segment.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{segment.description}</p>
                  <ul className="space-y-2">
                    {segment.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center text-gray-700">
                        <CheckCircle className="w-5 h-5 text-green-800 mr-2 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Delivery Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Dedicated delivery services for every material — fast, reliable, and right where you need it.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {services.map(s => (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`}>
                <Card className="h-full overflow-hidden border-2 border-gray-200 hover:border-green-800 hover:shadow-xl transition-all duration-300">
                  <div className="h-32 overflow-hidden">
                    <img loading="lazy" decoding="async" src={s.heroImage} alt={s.name} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                  </div>
                  <CardContent className="p-4 text-center">
                    <p className="font-bold text-gray-900 text-sm sm:text-base">{s.shortName}</p>
                    <p className="text-green-800 text-xs font-semibold mt-1">Delivery</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/delivery">
              <Button size="lg" className="bg-green-800 hover:bg-green-900 text-white">
                View All Delivery Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Products Preview Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Premium Materials</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From driveways to garden beds, we have the perfect materials for every landscaping project.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.slice(0, 6).map((product) => (
              <Card key={product.id} className="overflow-hidden hover:shadow-xl transition-shadow duration-300 border-2 border-gray-100">
                <div className="h-56 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{product.description}</p>
                  <Link to="/products">
                    <Button variant="outline" className="w-full border-green-800 text-green-800 hover:bg-green-800 hover:text-white">
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/products">
              <Button size="lg" className="bg-green-800 hover:bg-green-900 text-white">
                View All Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Delivery CTA Section */}
      <section
        className="py-24 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://images.unsplash.com/photo-1671022412547-75c4cb01f47e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxkdW1wJTIwdHJ1Y2slMjBkZWxpdmVyeXxlbnwwfHx8fDE3ODAxODg3NTN8MA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Professional Delivery Services
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Our experienced drivers deliver materials exactly where you need them. Same-day delivery available in Columbus and Muscogee County.
          </p>
          <Link to="/delivery">
            <Button size="lg" className="bg-green-700 hover:bg-green-800 text-white text-lg px-8 py-6">
              Learn About Delivery
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">From Our Yard to Your Property</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Premium materials, professional delivery, beautiful results.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[...gallery.materials.slice(0, 2), ...gallery.delivery.slice(0, 2), ...gallery.projects.slice(0, 2)].map((img, idx) => (
              <Link key={idx} to="/gallery" data-testid={`home-gallery-${idx}`}>
                <div className="relative h-48 sm:h-56 overflow-hidden rounded-lg group cursor-pointer">
                  <img loading="lazy" decoding="async" src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                    <p className="text-white font-semibold text-sm">{img.caption}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/gallery">
              <Button size="lg" variant="outline" className="border-2 border-green-800 text-green-800 hover:bg-green-800 hover:text-white">
                View Full Gallery
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
            <p className="text-lg text-gray-600 mb-6">Trusted by homeowners, contractors, and landscapers across the region.</p>
            <a
              href={companyInfo.googleBusinessUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-gray-200 rounded-full hover:border-green-800 hover:shadow-md transition-all"
              data-testid="home-google-reviews"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="font-semibold text-gray-900 text-sm">See all reviews on Google</span>
              <ArrowRight className="w-4 h-4 text-green-800" />
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 border-gray-100">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.content}"</p>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-10">
            <a
              href={companyInfo.googleBusinessUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-google-reviews-bottom"
            >
              <Button size="lg" variant="outline" className="border-2 border-green-800 text-green-800 hover:bg-green-800 hover:text-white">
                View Our Google Business Profile
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Blog Preview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Expert Guides & Tips</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Learn how to choose the right materials and plan your project with our local expert guides.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {blogPosts.slice(0, 3).map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`} data-testid={`home-blog-${post.id}`}>
                <Card className="h-full overflow-hidden border-2 border-gray-100 hover:border-green-800 hover:shadow-xl transition-all duration-300">
                  <div className="h-48 overflow-hidden">
                    <img loading="lazy" decoding="async" src={post.image} alt={post.title} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                  </div>
                  <CardContent className="p-6">
                    <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full mb-3">
                      {post.category}
                    </span>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight">{post.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">{post.excerpt}</p>
                    <span className="text-green-800 font-semibold text-sm inline-flex items-center">
                      Read More <ArrowRight className="ml-1 w-4 h-4" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/blog" data-testid="home-blog-view-all">
              <Button size="lg" variant="outline" className="border-2 border-green-800 text-green-800 hover:bg-green-800 hover:text-white">
                View All Guides
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Get answers to common questions about our materials and services.</p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`} className="border-2 border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-green-800">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Calculator CTA Banner */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-green-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center justify-center w-14 h-14 bg-green-700 rounded-full mb-4">
                <CalcIcon className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Not Sure How Much You Need?
              </h2>
              <p className="text-green-100 text-lg leading-relaxed">
                Use our free Material Calculator — enter your length, width, and depth and we'll calculate the exact cubic yards and tonnage for any material. Then request a quote in one click.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 sm:justify-end">
              <Link to="/calculator" data-testid="home-calc-cta">
                <Button size="lg" className="bg-white hover:bg-gray-100 text-green-900 text-lg px-8 py-6">
                  <CalcIcon className="mr-2 w-5 h-5" />
                  Try the Calculator
                </Button>
              </Link>
              <a href="tel:+17063663685">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-900 text-lg px-8 py-6">
                  <Phone className="mr-2 w-5 h-5" />
                  Call Instead
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-green-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Get a free quote today and experience the Midtown Earth Solutions difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white hover:bg-gray-100 text-green-900 text-lg px-8 py-6">
                Request a Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+17063663685">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-900 text-lg px-8 py-6">
                <Phone className="mr-2 w-5 h-5" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
